/*
 *  -----------------------------------------------------------------------
 *    <copyright file="trend-line-chart.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import Highcharts from 'highcharts/esm/highcharts.js';
import NavigationService from '@components-clean/page-nav/navigation.service';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import HighchartsReact from 'highcharts-react-official';
import React, { useEffect, useRef } from 'react';

interface ITrendLineChart {
    data: any;
    height: number;
    width?: number;
    getRef?: Function | null;
    title?: string;
    customKey?: string;
    toFixed?: boolean;
    hidePlotTitle?: boolean;
}

const TrendLineChart = ({
    data,
    height,
    width,
    getRef,
    title,
    customKey,
    toFixed = false,
    hidePlotTitle,
}: ITrendLineChart) => {
    const chart = useRef<HighchartsReact.RefObject>(null);

    useEffect(() => {
        if (getRef && chart) {
            getRef(chart?.current);
            setTimeout(() => {
                if (chart.current) chart.current.chart.reflow();
            }, 0);
        }
    }, [chart.current]);

    useEffect(() => {
        const subscribe = NavigationService.onUpdateSideBar().subscribe(() => {
            setTimeout(() => {
                if (chart.current) chart.current.chart.reflow();
            }, 0);
        });
        const subscribeReflow = NavigationService.legacyOnReflowChart().subscribe(() => {
            setTimeout(() => {
                if (chart.current) chart.current.chart.reflow();
            }, 0);
        });
        return () => {
            subscribeReflow.unsubscribe();
            subscribe.unsubscribe();
        };
    }, []);

    return (
        <HighchartsReact
            ref={chart}
            key={customKey}
            highcharts={Highcharts}
            constructorType="chart"
            options={{
                credits: { enabled: false },
                exporting: {
                    chartOptions: { title: { text: title, style: { fontWeight: 'semi-bold' } } },
                    enabled: true,
                    fallbackToExportServer: false,
                    buttons: {
                        contextButton: {
                            enabled: false,
                        },
                    },
                },
                chart: {
                    type: 'line',
                    height,
                    width: width ?? null,
                    backgroundColor: 'transparent',
                    plotBackgroundColor: 'white',
                    plotBorderColor: '#c3c3c8',
                    plotShadow: false,
                },
                title: hidePlotTitle ? null : (title ?? null),
                pointStart: 0,
                xAxis: [
                    {
                        type: 'datetime',
                        labels: {
                            format: '{value:%d %b %Y}',
                        },
                        minPadding: 0.05,
                        maxPadding: 0.05,
                        visible: true,
                        gridLineColor: 'transparent',
                        gridLineWidth: 0,
                        pointStart: 0,
                    },
                ],
                yAxis: [
                    {
                        minPadding: 0.05,
                        maxPadding: 0.05,
                        offset: -14,
                        title: null,
                        visible: true,
                        tickInterval: 10,
                        min: 0,
                        max: 100,
                        gridLineColor: 'transparent',
                        gridLineWidth: 0,
                        pointStart: 0,
                        labels: {
                            style: { color: '#616161' },
                            format: '{value} -',
                        },
                    },
                ],
                tooltip: {
                    formatter() {
                        const self: any = this;
                        return `<div>Data Set Date: ${self.point.date}</div><br><div>Score: ${
                            !toFixed ? self.point.y : self.point.y.toFixed(2)
                        }</div>`;
                    },
                },
                series: [
                    {
                        type: 'line',
                        data,
                        label: null,
                        animation: true,
                        showInLegend: false,
                        lineWidth: 1,
                        pointStart: 0,
                    },
                ],
                plotOptions: {
                    series: {
                        marker: {
                            radius: 6,
                        },
                    },
                },
            }}
        />
    );
};

export default React.memo(TrendLineChart);
